import type { HIWorkOrderCreatePayload, HIWorkOrderUpdatePayload } from '@/pages/homeInspection/hi.schema';

export type PendingLinkedHiEntry = {
  id: string;
  fingerprint: string;
};

export type PendingLinkedHiByIndex = Record<number, PendingLinkedHiEntry>;

export const fingerprintHiCreatePayload = (payload: HIWorkOrderCreatePayload): string =>
  JSON.stringify(payload);

export const fingerprintHiUpdatePayload = (payload: HIWorkOrderUpdatePayload): string =>
  JSON.stringify(payload);

/** Skip HI PATCH when mapped update payload matches the baseline from edit load. */
export const shouldSkipExistingHiUpdate = (
  baselineFingerprint: string | undefined,
  nextFingerprint: string
): boolean => Boolean(baselineFingerprint && baselineFingerprint === nextFingerprint);

/** Reuse pending HI only when the mapped create payload is unchanged. */
export const shouldReusePendingLinkedHi = (
  pending: PendingLinkedHiEntry | undefined,
  nextFingerprint: string
): boolean => Boolean(pending?.id && pending.fingerprint === nextFingerprint);

/**
 * If an HI was already created for this WO index, keep linking it even when the
 * checkbox was unchecked — avoids abandoning an orphan HI.
 */
export const shouldForceIncludePendingHi = (
  includeChecked: boolean,
  pending: PendingLinkedHiEntry | undefined
): boolean => !includeChecked && Boolean(pending?.id);

/** Attach per-index linkedHIWorkOrderID values onto the matching work orders. */
export const attachLinkedHiWorkOrderIds = <T extends object>(
  workOrders: T[],
  linkedHiByIndex: Record<number, string | undefined>
): (T & { linkedHIWorkOrderID?: string })[] =>
  workOrders.map((wo, index) => {
    const linkedHIWorkOrderID = linkedHiByIndex[index];
    return linkedHIWorkOrderID ? { ...wo, linkedHIWorkOrderID } : { ...wo };
  });

/**
 * Edit-flow: set or clear linkedHIWorkOrderID for every index present in the map.
 * `null` clears the link (Include Home Inspection unchecked).
 * Uncheck intentionally only clears the PS linkage; it does not call a HI delete/cancel API.
 */
export const applyLinkedHiWorkOrderIds = <T extends object>(
  workOrders: T[],
  linkedHiByIndex: Record<number, string | null | undefined>
): (T & { linkedHIWorkOrderID?: string | null })[] =>
  workOrders.map((wo, index) => {
    if (!(index in linkedHiByIndex)) return { ...wo };
    return { ...wo, linkedHIWorkOrderID: linkedHiByIndex[index] ?? null };
  });
